import { formatCopy } from '@/i18n/ns';
import type { AgentPermissionsCopy } from '@/i18n/ns/agent-permissions';
import type { AgentRequestsCopy } from '@/i18n/ns/agent-requests';
import { whenLabel } from '../inbox-model';
import type { InboxItem, InboxUpdate } from '../inbox-api';

// The words around an Agent request or its activity in the Inbox list and canvas heading.

const FAMILIES: Record<string, keyof AgentRequestsCopy> = {
  'library:read': 'familyLibraryRead',
  'library:write': 'familyLibraryWrite',
  'apps:author': 'familyAppsAuthor',
  'data:connections': 'familyDataConnections',
};
export const familyLabel = (copy: AgentRequestsCopy, family: string | null | undefined) =>
  copy[FAMILIES[family ?? ''] ?? 'familyOther'];

/**
 * A native Poneva action (no connection) in the person's language, from the same `action_<tool>`
 * labels the Connections well uses; null for a connection action (the server's "Connection ›
 * Title" stands) or a tool without a label.
 */
export function nativeActionLabel(
  copy: AgentPermissionsCopy,
  key: { connectionId?: string | null; action?: string | null } | null | undefined,
) {
  if (!key?.action || key.connectionId) return null;
  return (copy as Record<string, string>)[`action_${key.action}`] ?? null;
}

const isUpdate = (entry: InboxItem | InboxUpdate): entry is InboxUpdate => 'read' in entry;

/** "Claude Code" and the one detail after the kind: the family for a request, the outcome for activity. */
export function rowMeta(
  copy: AgentRequestsCopy,
  entry: InboxItem | InboxUpdate,
  permissions?: AgentPermissionsCopy,
) {
  if (isUpdate(entry)) {
    const activity = entry.activity;
    const detail =
      activity?.outcome === 'allowed'
        ? copy.allowedWithoutAsking
        : activity?.outcome === 'automatic'
          ? copy.ranAutomatically
          : activity?.outcome === 'refused'
            ? activity.reason === 'connection_read' && activity.connectionName
              ? formatCopy(copy.refusedRead, { name: activity.connectionName })
              : copy.refusedNothing
            : undefined;
    // A native action reads its own name in the person's language, as its request does.
    const title = permissions && nativeActionLabel(permissions, activity);
    return { by: entry.agent?.name, detail, ...(title ? { title } : {}) };
  }
  const title = permissions && nativeActionLabel(permissions, entry.ref);
  return {
    by: entry.ref?.agentName,
    detail: entry.ref?.family ? familyLabel(copy, entry.ref.family) : undefined,
    ...(title ? { title } : {}),
  };
}

/** The canvas heading of a request: waiting, who from and when, and what it is about. */
export function requestHeading(
  copy: AgentRequestsCopy,
  item: InboxItem,
  locale: string,
  permissions?: AgentPermissionsCopy,
) {
  const agent = item.ref?.agentName || copy.unnamedAgent;
  const when = whenLabel(item.createdAt, locale);
  // A native action reads its own translated name; a connection action keeps the server's title.
  const title = permissions && nativeActionLabel(permissions, item.ref);
  return {
    meta: `${copy.waitingForDecision} · ${formatCopy(when ? copy.fromAgent : copy.fromAgentOnly, { agent, when })}`,
    description: formatCopy(copy.describe, { agent }),
    ...(title ? { title } : {}),
  };
}
